import { useState, useEffect } from 'react';
import { 
  Card, 
  ListGroup, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  Row,
  Col
} from 'react-bootstrap';
import { 
  FaFilePdf, 
  FaFileImage, 
  FaFileAlt, 
  FaClock, 
  FaEye,
  FaExclamationTriangle,
  FaSyncAlt
} from 'react-icons/fa';

// Services
import { toast } from 'sonner';
import TransactionDocumentService from '../../../service/api/Documents/transactionDocumentService';

/**
 * Composant pour l'affichage et la gestion des documents d'une transaction
 * Affiche les preuves et documents associés à une transaction avec URLs signées sécurisées
 * 
 * @param {Object} props
 * @param {number} props.transactionId - ID de la transaction
 * @param {number} props.domainId - ID du domaine pour la sécurité
 * @param {string} props.className - Classes CSS supplémentaires
 */
const TransactionDocuments = ({ transactionId, domainId, className = '' }) => {
  // États pour la gestion des données et du chargement
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Charge les documents de la transaction depuis l'API
   * Gère les états de chargement et les erreurs
   */
  const fetchDocuments = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      setError(null);
      
      const docs = await TransactionDocumentService.getTransactionDocuments(transactionId, domainId);
      setDocuments(docs);
      
    } catch (err) {
      console.error('Erreur détaillée:', err);
      setError(err.message || 'Erreur lors du chargement des documents');
      
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /**
   * Effet pour le chargement initial des documents
   * Se déclenche quand transactionId ou domainId change
   */
  useEffect(() => {
    if (transactionId && domainId) {
      fetchDocuments();
    }
  }, [transactionId, domainId]);

  /**
   * Rafraîchit les documents et régénère les URLs signées
   */
  const handleRefreshDocuments = () => {
    fetchDocuments(true);
    toast.info('Rafraîchissement des liens documents');
  };

  /**
   * Ouvre un document dans un nouvel onglet
   * @param {Object} document - Document à ouvrir
   */
  const handleOpenDocument = async (document) => {
    try {
      await TransactionDocumentService.openTransactionDocument(document.url, document.fileName);
      toast.success('Document ouvert dans un nouvel onglet');
    } catch (err) {
      toast.error('Erreur lors de l\'ouverture du document');
    }
  };


  /**
   * Détermine l'icône appropriée selon le type de document
   * @param {string} documentType - Type du document
   * @returns {JSX.Element} Icône React
   */
  const getDocumentIcon = (documentType) => {
    const type = documentType?.toLowerCase();
    
    switch (type) {
      case 'pdf':
        return <FaFilePdf className="text-danger me-2" size={18} />;
      case 'image':
        return <FaFileImage className="text-success me-2" size={18} />;
      default:
        return <FaFileAlt className="text-primary me-2" size={18} />;
    }
  };

  /**
   * Calcule et formate le temps restant avant expiration d'une URL
   * @param {string} expiryTime - Date d'expiration au format ISO
   * @returns {string} Temps restant formaté
   */
  const getTimeRemaining = (expiryTime) => {
    const now = new Date();
    const expiry = new Date(expiryTime);
    const diffMs = expiry - now;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins <= 0) return 'Expiré';
    if (diffMins < 60) return `${diffMins} min`;
    
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours} h`;
  };

  /**
   * Vérifie si une URL a expiré
   * @param {string} expiryTime - Date d'expiration au format ISO
   * @returns {boolean} True si l'URL a expiré
   */
  const isUrlExpired = (expiryTime) => {
    return new Date(expiryTime) < new Date();
  };

  // Rendu de l'état de chargement
  if (loading) {
    return (
      <Card className={`mt-4 ${className}`}>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 mb-0 text-muted">Chargement des documents...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={`mt-4 border-0 shadow-sm ${className}`}>
      {/* En-tête de la section documents */}
      <Card.Header className="bg-light text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0 d-flex align-items-center">
          <FaFilePdf className="me-2" />
          Documents de la transaction
        </h5>
        
        <div className="d-flex align-items-center">
          {/* Badge du nombre de documents */}
          {documents.length > 0 && (
            <Badge bg="light" text="dark" className="me-2">
              {documents.length} document(s)
            </Badge>
          )}
          
          {/* Bouton de rafraîchissement */}
          <Button 
            variant="outline-light" 
            size="sm" 
            onClick={handleRefreshDocuments}
            disabled={refreshing}
          >
            <FaSyncAlt className={`me-1 ${refreshing ? 'fa-spin' : ''}`} />
            {refreshing ? 'Rafraîchissement...' : 'Rafraîchir'}
          </Button>
        </div>
      </Card.Header>
      
      <Card.Body className="p-0">
        {/* Message d'erreur */}
        {error && (
          <Alert variant="warning" className="m-3 mb-0">
            <div className="d-flex justify-content-between align-items-center">
              <span className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                {error}
              </span>
              <Button 
                variant="outline-warning" 
                size="sm" 
                onClick={() => fetchDocuments()}
              >
                Réessayer
              </Button>
            </div>
          </Alert>
        )}

        {/* Message si aucun document */}
        {!error && documents.length === 0 && (
          <div className="text-center py-5 text-muted">
            <FaFilePdf size={48} className="mb-3 opacity-25" />
            <p className="mb-0">Aucun document disponible pour cette transaction</p>
            <small>
              Les preuves de transaction apparaîtront ici si elles ont été ajoutées lors de la création
            </small>
          </div>
        )}

        {/* Liste des documents */}
        {!error && documents.length > 0 && (
          <ListGroup variant="flush">
            {documents.map((document, index) => {
              const isExpired = isUrlExpired(document.expiryTime);
              const timeRemaining = getTimeRemaining(document.expiryTime);
              
              return (
                <ListGroup.Item key={index} className="document-item">
                  <Row className="align-items-center">
                    {/* Informations du document */}
                    <Col md={6}>
                      <div className="d-flex align-items-start">
                        {getDocumentIcon(document.type)}
                        <div className="flex-grow-1">
                          <div className="fw-semibold text-dark">{document.name}</div>
                          <small className="text-muted d-block">
                            {document.fileName}
                          </small>
                          <div className="mt-1">
                            <Badge 
                              bg={isExpired ? "danger" : "light"} 
                              text={isExpired ? "white" : "dark"}
                              className="me-2"
                            >
                              <FaClock className="me-1" size={12} />
                              {timeRemaining}
                            </Badge>
                            <Badge bg="outline-secondary" className="text-capitalize">
                              {document.type?.toLowerCase()}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Col>
                    
                    {/* Actions sur le document */}
                    <Col md={6}>
                      <div className="d-flex justify-content-end gap-2">
                        {/* Badge d'expiration */}
                        {!isExpired && (
                          <Badge bg="light" text="dark" className="d-flex align-items-center me-2">
                            <FaClock className="me-1" size={12} />
                            {timeRemaining}
                          </Badge>
                        )}
                        
                        {/* Bouton Voir */}
                        <Button
                          variant={isExpired ? "outline-danger" : "outline-primary"}
                          size="sm"
                          onClick={() => handleOpenDocument(document)}
                          disabled={isExpired}
                          className="d-flex align-items-center"
                        >
                          <FaEye className="me-1" />
                          {isExpired ? 'Expiré' : 'Voir'}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
        
        {/* Pied de page informatif */}
        {documents.length > 0 && (
          <div className="p-3 bg-light border-top">
            <Row className="align-items-center">
              <Col>
                <small className="text-muted">
                  <FaClock className="me-1" />
                  Les liens expirent automatiquement après 30 minutes pour des raisons de sécurité.
                </small>
              </Col>
              <Col xs="auto">
                <small className="text-muted">
                  Dernière mise à jour : {new Date().toLocaleTimeString()}
                </small>
              </Col>
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TransactionDocuments;